import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    FormData as DbFormData,
    FormDataFlagRequest,
    FormDataSignRequest,
    MutationResult,
    QueryHookParams,
    QueryResult,
} from "../models";
import { useQueryClient } from "@tanstack/react-query";

export interface IUseFormDataQuery {
    listQuery: QueryResult<DbFormData[]>;
    flagMutation: MutationResult<DbFormData, unknown, FormDataFlagRequest>;
    signMutation: MutationResult<DbFormData, unknown, FormDataSignRequest>;
    unlockSignedMutation: MutationResult<DbFormData, unknown, FormDataSignRequest>;
}

interface UseFormDataQueryParams extends QueryHookParams {
    caseId?: number | string;
    formSlug?: string;
    tissueId?: number | string;
    procedureId?: number | string;
    shipmentId?: number | string;
    getOrCreate?: boolean;
    refetchFormData?: () => Promise<void>;
    archivePdf?: () => void;
}

export const useFormDataQuery = (params?: UseFormDataQueryParams): IUseFormDataQuery => {
    const formDataId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const caseId = isNaN(Number(params?.caseId)) ? 0 : Number(params?.caseId);
    const tissueId = isNaN(Number(params?.tissueId)) ? 0 : Number(params?.tissueId);
    const procedureId = isNaN(Number(params?.procedureId)) ? 0 : Number(params?.procedureId);
    const shipmentId = isNaN(Number(params?.shipmentId)) ? 0 : Number(params?.shipmentId);
    const formSlug = params?.formSlug ?? "";
    const queryClient = useQueryClient();
    const { get, put } = useAxios();
    const url = Endpoints.FormData;

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<DbFormData[]>({
                url,
                config: {
                    ...config,
                    params: {
                        caseId,
                        formSlug,
                        tissueId,
                        procedureId,
                        shipmentId,
                        getOrCreate: params?.getOrCreate ? "true" : "false",
                    },
                },
            }),
        queryKey: queryKeys.formData.list({ caseId, formSlug, tissueId }),
        placeholderData: [],
        enabled: !!caseId && !!formSlug,
        staleTime: 2000,
        cacheTime: 2000,
    });

    const flagMutation = useGenericMutation({
        mutationFn: (data: FormDataFlagRequest) => put<FormDataFlagRequest, DbFormData>({ url: `${url}/flag`, data }),
        queryKey: queryKeys.formData.detail(formDataId),
        keysToInvalidate: [],
        successToast: "Form flagged successfully",
        onSettled: () => {
            if (!params?.refetchFormData) return;
            params.refetchFormData().then(() => {
                queryClient.invalidateQueries(queryKeys.cases.forms(caseId));
            });
        },
    });

    const signMutation = useGenericMutation({
        mutationFn: (data: FormDataSignRequest) => put<FormDataSignRequest, DbFormData>({ url: `${url}/sign`, data }),
        queryKey: queryKeys.formData.detail(formDataId),
        keysToInvalidate: [],
        disableSuccessToast: true,
        onSettled: () => {
            if (!params?.refetchFormData) return;
            params.refetchFormData().then(() => {
                queryClient.invalidateQueries(queryKeys.cases.forms(caseId));
            });
        },
    });

    const unlockSignedMutation = useGenericMutation({
        mutationFn: (data: FormDataSignRequest) =>
            put<FormDataSignRequest, DbFormData>({ url: `${url}/unlock-signed`, data }),
        queryKey: queryKeys.formData.detail(formDataId),
        keysToInvalidate: [],
        disableSuccessToast: true,
        onSettled: () => {
            if (!params?.refetchFormData) return;
            params.refetchFormData().then(() => {
                queryClient.invalidateQueries(queryKeys.cases.forms(caseId));
            });
        },
    });

    return useMemo<IUseFormDataQuery>(
        () => ({
            listQuery,
            flagMutation,
            signMutation,
            unlockSignedMutation,
        }),
        [flagMutation, listQuery, signMutation, unlockSignedMutation]
    );
};
